/**
 * Responsibility-area configuration API helpers (RESP1).
 *
 * Two endpoints. The list is read by BOTH the settings page and the task
 * form — there is no separate suggestion call, because 13 items are a
 * trivial payload and a second endpoint over the same data would be a
 * second thing to keep true.
 *
 * The organisation is never sent: it comes from the JWT on the server.
 * Neither is the category code in the update body — it is the path.
 *
 * Same conventions as ./workReports.js: raw fetch, Bearer headers from
 * ./core, a thrown Error carrying the parsed German message and
 * `err.status` for status-specific UI.
 */
import { API_BASE_URL, getApiHeaders, parseAdminErrorResponse } from "./core";

async function _throwFrom(res) {
  const err = new Error(await parseAdminErrorResponse(res));
  err.status = res.status;
  throw err;
}

/**
 * GET /api/admin/responsibility-areas → `{items, total}`.
 *
 * Always all 13 categories, configured or not. An entry with
 * `is_configured: false` is not missing data — it is the default the
 * organisation actually behaves by, which is why no backfill was needed.
 */
export async function fetchResponsibilityAreas() {
  const res = await fetch(`${API_BASE_URL}/api/admin/responsibility-areas`, {
    headers: getApiHeaders(),
  });
  if (!res.ok) await _throwFrom(res);
  const data = await res.json();
  return {
    items: Array.isArray(data?.items) ? data.items : [],
    total: typeof data?.total === "number" ? data.total : 0,
  };
}

/**
 * PUT /api/admin/responsibility-areas/{activity_category}.
 *
 * Full-state upsert: the edit form shows every field at once, so what
 * the user is looking at and what is sent are the same thing and there
 * is no absent-versus-null ambiguity.
 *
 * `participant_user_ids` must be an array — `[]` clears it, `null` is a
 * 422. Admin only; a manager gets 403.
 */
export async function updateResponsibilityArea(activityCategory, payload) {
  const res = await fetch(
    `${API_BASE_URL}/api/admin/responsibility-areas/${encodeURIComponent(
      activityCategory
    )}`,
    {
      method: "PUT",
      headers: getApiHeaders(),
      body: JSON.stringify({
        is_active: payload.is_active !== false,
        description: payload.description ?? null,
        default_assigned_to_user_id:
          payload.default_assigned_to_user_id || null,
        participant_user_ids: Array.isArray(payload.participant_user_ids)
          ? payload.participant_user_ids
          : [],
        apply_default_on_task_create:
          payload.apply_default_on_task_create !== false,
      }),
    }
  );
  if (!res.ok) await _throwFrom(res);
  return res.json();
}

// ═══ RESP2b endpoints — id-based CRUD ═════════════════════════════════
//
// The legacy PUT above stays for nothing but backwards compatibility;
// everything the management UI does goes through these. They answer with
// a STRUCTURED error body — `{code, message, details}` — where the legacy
// route answers with a bare string, so `_throwFromStructured` keeps the
// code on the Error for the callers that branch on it (a delete blocked
// by usage looks nothing like a name collision).

/** Parse `{code, message, details}` or fall back to the plain shape. */
async function _throwStructured(res) {
  let payload = null;
  try {
    payload = await res.json();
  } catch {
    payload = null;
  }
  const detail = payload?.detail;
  const err = new Error(
    (typeof detail === "object" ? detail?.message : detail) ||
      "Die Aktion konnte nicht ausgeführt werden."
  );
  err.status = res.status;
  if (typeof detail === "object" && detail) {
    err.code = detail.code || null;
    err.details = detail.details || null;
  }
  throw err;
}

/** POST /api/admin/responsibility-areas — create one custom area.
 *
 * The server owns the id, the slug and the compatibility key; sending
 * any of them is a 422 rather than a silently ignored field, so this
 * deliberately posts only what an organisation gets to decide.
 */
export async function createResponsibilityArea(payload) {
  const res = await fetch(`${API_BASE_URL}/api/admin/responsibility-areas`, {
    method: "POST",
    headers: getApiHeaders(),
    body: JSON.stringify({
      label: payload.label,
      description: payload.description ?? null,
      icon: payload.icon ?? null,
      color_token: payload.color_token ?? null,
      is_active: payload.is_active !== false,
      default_assigned_to_user_id: payload.default_assigned_to_user_id || null,
      default_participant_user_ids: Array.isArray(
        payload.default_participant_user_ids
      )
        ? payload.default_participant_user_ids
        : [],
      apply_default_on_task_create:
        payload.apply_default_on_task_create !== false,
    }),
  });
  if (!res.ok) await _throwStructured(res);
  return res.json();
}

/** PATCH /api/admin/responsibility-areas/{id} — partial by design.
 *
 * Only the keys present in `changes` are sent, because absent means
 * untouched and present-and-null means cleared. Building the object at
 * the call site rather than spreading a whole form keeps a field nobody
 * edited from being rewritten with its own value.
 */
export async function patchResponsibilityArea(areaId, changes) {
  const res = await fetch(
    `${API_BASE_URL}/api/admin/responsibility-areas/${encodeURIComponent(
      areaId
    )}`,
    {
      method: "PATCH",
      headers: getApiHeaders(),
      body: JSON.stringify(changes || {}),
    }
  );
  if (!res.ok) await _throwStructured(res);
  return res.json();
}

/** GET /api/admin/responsibility-areas/{id}/usage.
 *
 * Loaded for the SELECTED area only — one request per selection, never
 * one per row.
 */
export async function fetchResponsibilityAreaUsage(areaId) {
  const res = await fetch(
    `${API_BASE_URL}/api/admin/responsibility-areas/${encodeURIComponent(
      areaId
    )}/usage`,
    { headers: getApiHeaders() }
  );
  if (!res.ok) await _throwStructured(res);
  return res.json();
}

/** DELETE /api/admin/responsibility-areas/{id}.
 *
 * Refused with 409 for a system area and for one with any task or report
 * filed in it — `err.code` tells the two apart, and `err.details` carries
 * the counts. The UI never shows the raw message for those.
 */
export async function deleteResponsibilityArea(areaId) {
  const res = await fetch(
    `${API_BASE_URL}/api/admin/responsibility-areas/${encodeURIComponent(
      areaId
    )}`,
    { method: "DELETE", headers: getApiHeaders() }
  );
  if (!res.ok) await _throwStructured(res);
  return res.json();
}
